import { useSignal } from "@preact/signals";
import { useEffect } from "preact/hooks";

interface BeforeInstallPromptEvent extends Event {
  prompt(): void;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export default function InstallPrompt() {
  const deferredPrompt = useSignal<BeforeInstallPromptEvent | null>(null);
  const showInstallPrompt = useSignal(false);
  const isInstalled = useSignal(false);

  useEffect(() => {
    // Check if app is already installed
    if (globalThis.navigator && 'getInstalledRelatedApps' in globalThis.navigator) {
      const navigator = globalThis.navigator as Navigator & {
        getInstalledRelatedApps(): Promise<Array<{ platform: string }>>;
      };
      navigator.getInstalledRelatedApps().then((relatedApps) => {
        if (relatedApps.length > 0) {
          isInstalled.value = true;
        }
      });
    }

    // Check if running as standalone PWA
    if (globalThis.matchMedia?.('(display-mode: standalone)').matches) {
      isInstalled.value = true;
    }

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      deferredPrompt.value = e as BeforeInstallPromptEvent;
      showInstallPrompt.value = true;
    };

    // Listen for appinstalled event
    const handleAppInstalled = () => {
      showInstallPrompt.value = false;
      isInstalled.value = true;
      deferredPrompt.value = null;
    };

    globalThis.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    globalThis.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      globalThis.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      globalThis.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt.value) return;

    deferredPrompt.value.prompt();
    const { outcome } = await deferredPrompt.value.userChoice;
    
    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    } else {
      console.log('User dismissed the install prompt');
    }
    
    deferredPrompt.value = null;
    showInstallPrompt.value = false;
  };

  const handleDismiss = () => {
    showInstallPrompt.value = false;
  };

  // Don't show if already installed or no prompt available
  if (isInstalled.value || !showInstallPrompt.value) {
    return null;
  }

  return (
    <div class="mb-4 p-4 rounded-lg text-white bg-gradient-to-r from-blue-500 to-purple-600 dark:from-blue-600 dark:to-purple-700 transition-colors duration-200">
      <div class="flex items-center justify-between">
        <div class="flex-1">
          <h3 class="font-semibold text-sm">Install App</h3>
          <p class="text-xs opacity-90 mt-1">
            Add to your home screen for quick access
          </p>
        </div>
        <div class="flex gap-2">
          <button
            onClick={handleInstallClick}
            class="px-3 py-1 bg-white/20 hover:bg-white/30 rounded text-xs font-medium transition-colors flex items-center justify-center gap-1"
          >
            <i class="fas fa-download"></i>
            <span class="hidden md:inline">Install</span>
          </button>
          <button
            onClick={handleDismiss}
            class="px-2 py-1 bg-white/10 hover:bg-white/20 rounded text-xs transition-colors flex items-center justify-center"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>
    </div>
  );
}
